import * as React from 'react';
import { FormBuilderV2 } from '../FormBuilderV2/FormBuilderV2';
import { FormRendererV2 } from '../FormRendererV2/FormRendererV2';
import { FormDefinition, FormMediaRef } from '../../types/formSchema';
import { AppUser } from 'Src/views/App/App';
import {
  createFormTemplate,
  FormTemplateType,
  getFormTemplates,
  updateFormTemplate
} from 'Src/helpers/service/admin/formTemplates';
import { ConfirmationRequestTemplate } from 'Src/models/confirmationRequestTemplate';
import { formDefinitionToLegacyFields, legacyFieldsToFormDefinition } from 'Src/helpers/form/legacyFormAdapter';
import { validateFormDefinitionStructure } from 'Src/helpers/form/formDefinitionValidation';
import { stripUnsignedFormMediaKeys } from 'Src/helpers/form/formMedia';
import { SchoolFormsHelp } from '../SchoolFormsHelp/SchoolFormsHelp';
import { SimpleModal } from '../SimpleModal/SimpleModal';
import './SendTimeFormEditor.scss';

interface Props {
  definition: FormDefinition;
  onChange: (definition: FormDefinition) => void;
  uploadImage?: (file: File) => Promise<string | FormMediaRef>;
  uploadFile?: (file: File) => Promise<FormMediaRef>;
  user?: AppUser;
  templateType?: FormTemplateType;
  initialTemplateId?: string;
  defaultTemplateName?: string;
  onCancelRequested?: () => void;
  onSendRequested?: () => void;
  defaultBuilderMode?: 'COMPACT' | 'FULL';
  showTemplateSaveAction?: boolean;
  templateSaveButtonText?: string;
  selectDefaultTemplateOnLoad?: boolean;
  applyDefaultTemplateOnLoad?: boolean;
  emptyTemplateLabel?: string;
  emptyTemplateBuilderMode?: 'COMPACT' | 'FULL';
  footerBeforeActions?: React.ReactNode;
  formHeaderExtra?: React.ReactNode;
  sendButtonText?: string;
  showHelpAction?: boolean;
}

const DEFAULT_TEMPLATE_EDIT_ROLES = ['ADMIN'];
const DEFAULT_TEMPLATE_CREATE_ROLES = ['ADMIN'];

type PendingSendAction = 'SEND_ONLY' | 'SAVE_AND_SEND' | null;
type TemplateNameAction = 'SAVE_ONLY' | 'SAVE_AND_SEND' | null;

const getRequestErrorMessage = (error: any, fallback: string): string =>
  String(error?.response?.data?.details?.text || error?.response?.data?.message || error?.message || fallback);

const roleAllowed = (role: string | undefined, roles: string[] | undefined, fallback: string[]): boolean => {
  const current = String(role || '').toUpperCase();
  const allowed = roles && roles.length > 0 ? roles : fallback;
  return !!current && allowed.map(item => String(item).toUpperCase()).includes(current);
};

export const SendTimeFormEditor: React.FC<Props> = ({
  definition,
  onChange,
  uploadImage,
  uploadFile,
  user,
  templateType,
  initialTemplateId,
  defaultTemplateName = 'Form template',
  onCancelRequested,
  onSendRequested,
  defaultBuilderMode = 'COMPACT',
  showTemplateSaveAction = false,
  templateSaveButtonText = 'Save as template',
  selectDefaultTemplateOnLoad = true,
  applyDefaultTemplateOnLoad = false,
  emptyTemplateLabel = 'Start from scratch',
  emptyTemplateBuilderMode = 'FULL',
  footerBeforeActions,
  formHeaderExtra,
  sendButtonText = 'Send',
  showHelpAction = true
}) => {
  const [mode, setMode] = React.useState<'EDIT' | 'PREVIEW'>('EDIT');
  const [builderMode, setBuilderMode] = React.useState<'COMPACT' | 'FULL'>(defaultBuilderMode);
  const [toolbarMode, setToolbarMode] = React.useState<'SIMPLE' | 'EDIT' | 'PREVIEW'>(
    defaultBuilderMode === 'COMPACT' ? 'SIMPLE' : 'EDIT'
  );
  const [previewAnswers, setPreviewAnswers] = React.useState({});
  const initialDefinition = React.useRef<FormDefinition>(definition);
  const [templates, setTemplates] = React.useState<ConfirmationRequestTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState(initialTemplateId || '');
  const [isSavingTemplate, setIsSavingTemplate] = React.useState(false);
  const [pendingSendAction, setPendingSendAction] = React.useState<PendingSendAction>(null);
  const [isTemplateNameModalOpen, setIsTemplateNameModalOpen] = React.useState(false);
  const [newTemplateName, setNewTemplateName] = React.useState('');
  const [templateNameAction, setTemplateNameAction] = React.useState<TemplateNameAction>(null);
  const [templateNameError, setTemplateNameError] = React.useState('');
  const [templateSaveError, setTemplateSaveError] = React.useState('');

  const selectedTemplate = templates.find(template => template.id === selectedTemplateId);
  const canEditSelectedTemplate =
    !!selectedTemplate && roleAllowed(user?.activeRole, selectedTemplate.editableByRoles, DEFAULT_TEMPLATE_EDIT_ROLES);
  const canCreateTemplates = roleAllowed(
    user?.activeRole,
    selectedTemplate?.creatableByRoles,
    DEFAULT_TEMPLATE_CREATE_ROLES
  );
  const canSaveTemplate =
    canEditSelectedTemplate || (!selectedTemplateId && canCreateTemplates && !!user && !!templateType);
  const canSaveAndSend =
    canEditSelectedTemplate || (!selectedTemplateId && canCreateTemplates && !!user && !!templateType);
  const hasTemplateChanges = React.useMemo(
    () => JSON.stringify(definition) !== JSON.stringify(initialDefinition.current),
    [definition]
  );

  React.useEffect(() => {
    if (!user || !templateType) return;
    getFormTemplates(user, templateType).then(items => {
      setTemplates(items);
      const selected =
        items.find(item => item.id === initialTemplateId) ||
        (selectDefaultTemplateOnLoad ? items.find(item => item.isDefault) || items[0] : undefined);
      if (selected) {
        const selectedDefinition =
          selected.formDefinition ||
          legacyFieldsToFormDefinition(selected.fields || [], { title: definition.title || 'Request form' });
        setSelectedTemplateId(selected.id);
        initialDefinition.current = selectedDefinition;
        if (applyDefaultTemplateOnLoad && !initialTemplateId && definition.elements.length === 0) {
          onChange(selectedDefinition);
        }
        setMode('EDIT');
        setBuilderMode(defaultBuilderMode);
        setToolbarMode(defaultBuilderMode === 'COMPACT' ? 'SIMPLE' : 'EDIT');
      }
    });
  }, [
    user,
    templateType,
    initialTemplateId,
    selectDefaultTemplateOnLoad,
    applyDefaultTemplateOnLoad,
    defaultBuilderMode
  ]);

  React.useEffect(() => {
    if (selectedTemplateId !== '' || (!templateType && templates.length === 0)) return;
    setMode('EDIT');
    setBuilderMode(emptyTemplateBuilderMode);
    setToolbarMode(emptyTemplateBuilderMode === 'COMPACT' ? 'SIMPLE' : 'EDIT');
  }, [selectedTemplateId, templateType, templates.length, emptyTemplateBuilderMode]);

  const resetToTemplate = () => {
    onChange(initialDefinition.current);
    setPreviewAnswers({});
    setMode('EDIT');
  };

  const selectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setPreviewAnswers({});
    setMode('EDIT');
    setTemplateSaveError('');

    if (!templateId) {
      const blank = legacyFieldsToFormDefinition([], { title: definition.title || 'Request form' });
      initialDefinition.current = blank;
      onChange(blank);
      setBuilderMode(emptyTemplateBuilderMode);
      setToolbarMode(emptyTemplateBuilderMode === 'COMPACT' ? 'SIMPLE' : 'EDIT');
      return;
    }

    const template = templates.find(item => item.id === templateId);
    if (!template) return;
    const templateDefinition =
      template.formDefinition ||
      legacyFieldsToFormDefinition(template.fields || [], { title: definition.title || 'Request form' });
    initialDefinition.current = templateDefinition;
    onChange(templateDefinition);
    setBuilderMode(defaultBuilderMode);
    setToolbarMode(defaultBuilderMode === 'COMPACT' ? 'SIMPLE' : 'EDIT');
  };

  const getSuggestedTemplateName = (): string => `${defaultTemplateName} ${templates.length + 1}`;

  const saveSelectedTemplate = (nameOverride?: string) => {
    if (!user || !templateType) return Promise.resolve();

    const sanitizedDefinition = stripUnsignedFormMediaKeys(definition);
    const structureIssues = validateFormDefinitionStructure(sanitizedDefinition);
    if (structureIssues.length > 0) {
      const message = structureIssues.map(issue => issue.message).join(' ');
      setTemplateSaveError(message);
      return Promise.reject(new Error(message));
    }

    setTemplateSaveError('');
    setIsSavingTemplate(true);

    const payload = {
      schemaVersion: 2 as const,
      formDefinition: sanitizedDefinition,
      fields: formDefinitionToLegacyFields(sanitizedDefinition) as any
    };

    const request = selectedTemplateId
      ? updateFormTemplate(user, templateType, selectedTemplateId, payload)
      : createFormTemplate(user, templateType, {
          name: (nameOverride || '').trim() || getSuggestedTemplateName(),
          ...payload
        });

    return request.then(
      saved => {
        setIsSavingTemplate(false);
        setTemplateSaveError('');
        if (!selectedTemplateId) setSelectedTemplateId(saved.id);
        setTemplates(previous => {
          const next = previous.slice();
          const index = next.findIndex(item => item.id === saved.id);
          if (index >= 0) {
            next[index] = saved;
          } else {
            next.push(saved);
          }
          return next;
        });
        initialDefinition.current = saved.formDefinition || sanitizedDefinition;
        onChange(saved.formDefinition || sanitizedDefinition);
      },
      error => {
        setIsSavingTemplate(false);
        setTemplateSaveError(getRequestErrorMessage(error, 'Failed to save form template.'));
        throw error;
      }
    );
  };

  const sendOnly = () => {
    if (!onSendRequested) return;
    if (canSaveAndSend && hasTemplateChanges) {
      setPendingSendAction('SEND_ONLY');
      return;
    }
    onSendRequested();
  };

  const openTemplateNameModal = (action: TemplateNameAction) => {
    setNewTemplateName(getSuggestedTemplateName());
    setTemplateNameAction(action);
    setTemplateNameError('');
    setTemplateSaveError('');
    setIsTemplateNameModalOpen(true);
  };

  const saveTemplateFromAction = () => {
    if (!selectedTemplateId) {
      openTemplateNameModal('SAVE_ONLY');
      return;
    }

    saveSelectedTemplate().catch(() => undefined);
  };

  const saveChangesAndSend = () => {
    if (!onSendRequested || !canSaveAndSend) return;
    if (!hasTemplateChanges) {
      onSendRequested();
      return;
    }

    if (!selectedTemplateId) {
      openTemplateNameModal('SAVE_AND_SEND');
      return;
    }

    setPendingSendAction('SAVE_AND_SEND');
  };

  const closeSendConfirmation = () => setPendingSendAction(null);

  const confirmPendingSendAction = () => {
    if (!onSendRequested) return;

    const action = pendingSendAction;
    setPendingSendAction(null);

    if (action === 'SEND_ONLY') {
      onSendRequested();
      return;
    }

    if (action === 'SAVE_AND_SEND') {
      saveSelectedTemplate()
        .then(() => onSendRequested())
        .catch(() => undefined);
    }
  };

  const closeTemplateNameModal = () => {
    if (isSavingTemplate) return;
    setIsTemplateNameModalOpen(false);
    setTemplateNameAction(null);
  };

  const confirmCreateTemplateAndSend = () => {
    if (!onSendRequested) return;
    const trimmedName = newTemplateName.trim();

    if (!trimmedName) {
      setTemplateNameError('Please enter a template name.');
      return;
    }

    const action = templateNameAction;
    setIsTemplateNameModalOpen(false);
    setTemplateNameAction(null);

    saveSelectedTemplate(trimmedName)
      .then(() => {
        if (action === 'SAVE_AND_SEND') onSendRequested?.();
      })
      .catch(() => undefined);
  };

  const renderTemplateNameModal = () => {
    if (!isTemplateNameModalOpen) return null;

    return (
      <SimpleModal
        isOpen={true}
        title={templateNameAction === 'SAVE_AND_SEND' ? 'Save template and send' : 'Save template'}
        buttonText={isSavingTemplate ? 'Saving...' : templateNameAction === 'SAVE_AND_SEND' ? 'Save and send' : 'Save'}
        buttonCancelText="Cancel"
        onButtonClick={confirmCreateTemplateAndSend}
        onCloseClick={closeTemplateNameModal}
        customClass="SendTimeFormEditor__confirmDialog"
        dialogStyle={{ maxWidth: 460 }}
        isBodyBold={false}
      >
        <div className="SendTimeFormEditor__templateNameBody">
          <label htmlFor="send-time-template-name" className="SendTimeFormEditor__templateNameLabel">
            Template name
          </label>
          <input
            id="send-time-template-name"
            className="form-control"
            value={newTemplateName}
            maxLength={80}
            autoFocus
            onChange={event => {
              setNewTemplateName(event.target.value);
              setTemplateNameError('');
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                confirmCreateTemplateAndSend();
              }
            }}
          />
          {templateNameError ? (
            <div className="SendTimeFormEditor__templateNameError" role="alert">
              {templateNameError}
            </div>
          ) : null}
          <div className="SendTimeFormEditor__templateNameHint">
            This name will be shown in the template list for future sends.
          </div>
        </div>
      </SimpleModal>
    );
  };

  const renderSendConfirmationModal = () => {
    if (!pendingSendAction) return null;

    const templateName = selectedTemplate?.name || defaultTemplateName;
    const isSaveAndSend = pendingSendAction === 'SAVE_AND_SEND';
    const message = isSaveAndSend
      ? selectedTemplateId
        ? `Your changes will be applied to the template “${templateName}” and then the form will be sent.`
        : 'A new template will be created from this form and then the form will be sent.'
      : 'The form will be sent, but your changes will not be saved to the template.';

    return (
      <SimpleModal
        isOpen={true}
        title={isSaveAndSend ? 'Save changes and send?' : 'Send without saving?'}
        buttonText={isSaveAndSend ? 'Save and send' : 'Send without saving'}
        buttonCancelText="Cancel"
        onButtonClick={confirmPendingSendAction}
        onCloseClick={closeSendConfirmation}
        customClass="SendTimeFormEditor__confirmDialog"
        dialogStyle={{ maxWidth: 460 }}
        isBodyBold={false}
      >
        <div className="SendTimeFormEditor__confirmBody">
          <div className="SendTimeFormEditor__confirmIcon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M12 8v5" />
              <path d="M12 17h.01" />
              <path d="M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.4L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            </svg>
          </div>
          <div className="SendTimeFormEditor__confirmCopy">
            <div className="SendTimeFormEditor__confirmLabel">
              {isSaveAndSend ? 'Template update' : 'Unsaved template changes'}
            </div>
            <p className="SendTimeFormEditor__confirmMessage">{message}</p>
          </div>
        </div>
      </SimpleModal>
    );
  };

  return (
    <div className="SendTimeFormEditor">
      {renderSendConfirmationModal()}
      {renderTemplateNameModal()}

      <div className="SendTimeFormEditor__header">
        <div className="SendTimeFormEditor__titleActions">
          {templates.length > 0 || templateType ? (
            <select
              className="form-control form-control-sm SendTimeFormEditor__templateSelect"
              value={selectedTemplateId}
              onChange={event => selectTemplate(event.target.value)}
              aria-label="Form template"
            >
              <option value="">{emptyTemplateLabel}</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name || 'Untitled template'}
                  {template.isDefault ? ' (default)' : ''}
                </option>
              ))}
            </select>
          ) : null}
          <div className="SendTimeFormEditor__actionGroup">
            <button
              type="button"
              className="SendTimeFormEditor__toolbarButton SendTimeFormEditor__toolbarButton--ghost"
              onClick={resetToTemplate}
            >
              Reset
            </button>
            {showTemplateSaveAction && user && templateType && canSaveTemplate ? (
              <button
                type="button"
                className="SendTimeFormEditor__toolbarButton"
                onClick={saveTemplateFromAction}
                disabled={!hasTemplateChanges || isSavingTemplate}
              >
                {isSavingTemplate ? 'Saving...' : selectedTemplateId ? 'Save template changes' : templateSaveButtonText}
              </button>
            ) : null}
          </div>
        </div>
        <div className="SendTimeFormEditor__actions">
          <div className="SendTimeFormEditor__modeSwitch" role="group" aria-label="Form editor mode">
            <button
              type="button"
              className={toolbarMode === 'SIMPLE' ? 'is-active' : ''}
              onClick={() => {
                setMode('EDIT');
                setBuilderMode('COMPACT');
                setToolbarMode('SIMPLE');
              }}
            >
              Simple editor
            </button>
            <button
              type="button"
              className={toolbarMode === 'EDIT' ? 'is-active' : ''}
              onClick={() => {
                setMode('EDIT');
                setBuilderMode('FULL');
                setToolbarMode('EDIT');
              }}
            >
              Edit
            </button>
            <button
              type="button"
              className={toolbarMode === 'PREVIEW' ? 'is-active' : ''}
              onClick={() => {
                setPreviewAnswers({});
                setMode('PREVIEW');
                setToolbarMode('PREVIEW');
              }}
            >
              Preview
            </button>
          </div>
          {showHelpAction ? (
            <div className="SendTimeFormEditor__helpAction">
              <SchoolFormsHelp />
            </div>
          ) : null}
        </div>
      </div>

      {templateSaveError && (
        <div className="SendTimeFormEditor__error" role="alert">
          {templateSaveError}
        </div>
      )}

      {mode === 'EDIT' ? (
        <FormBuilderV2
          definition={definition}
          onChange={onChange}
          uploadImage={uploadImage}
          uploadFile={uploadFile}
          mode={builderMode}
          formHeaderExtra={formHeaderExtra}
        />
      ) : (
        <div className="SendTimeFormEditor__preview">
          <div className="SendTimeFormEditor__previewCard">
            <h2>{definition.title || 'Consent request questions'}</h2>
            {definition.description && <p>{definition.description}</p>}
            <FormRendererV2
              definition={definition}
              answers={previewAnswers}
              onChange={setPreviewAnswers}
              onSubmitRequested={() => undefined}
            />
          </div>
        </div>
      )}

      {footerBeforeActions && <div className="SendTimeFormEditor__beforeActions">{footerBeforeActions}</div>}

      {(onCancelRequested || onSendRequested) && (
        <div className="SendTimeFormEditor__footer">
          {onCancelRequested && (
            <button type="button" className="sit-btn sit-btn--secondary" onClick={onCancelRequested}>
              Cancel
            </button>
          )}
          {onSendRequested && canSaveAndSend && (
            <button
              type="button"
              className="sit-btn sit-btn--secondary"
              onClick={saveChangesAndSend}
              disabled={isSavingTemplate}
            >
              {isSavingTemplate
                ? 'Saving...'
                : selectedTemplateId
                ? `Save changes and ${sendButtonText.toLowerCase()}`
                : `Save as template and ${sendButtonText.toLowerCase()}`}
            </button>
          )}
          {onSendRequested && (
            <button type="button" className="sit-btn sit-btn--primary" onClick={sendOnly} disabled={isSavingTemplate}>
              {sendButtonText}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
